import { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import {
  activateIngredientCatalogItems,
  getIngredientCatalog,
  getIngredientCatalogCategories,
} from "../api/ingredientsApi";
import {
  INGREDIENT_SECTORS,
  normalizeIngredientRow,
} from "../utils/ingredientFormatters";
import { errorToMessage, unwrapHttp } from "../utils/httpResponse";
import AddInsumosTable from "./AddInsumosTable";

const CATALOG_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 180;

function normalizeList(response) {
  const data = unwrapHttp(response);
  const list = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
    ? data
    : [];

  return {
    rows: list.map(normalizeIngredientRow).filter((ingredient) => ingredient?.id != null),
    totalItems: data?.meta?.totalItems ?? list.length,
    totalPages: data?.meta?.totalPages ?? (list.length > 0 ? 1 : 0),
  };
}

export default function AddInsumosModal({ open, onClose, onConfirmed }) {
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sector, setSector] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    setCatalog([]);
    setCategories([]);
    setSelectedIds([]);
    setSearchInput("");
    setSearch("");
    setCategory("");
    setSector("");
    setPage(1);
    setTotalItems(0);
    setTotalPages(0);
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    setCategories([]);

    getIngredientCatalogCategories({
      sector: sector || undefined,
      signal: controller.signal,
    })
      .then((response) => {
        const data = unwrapHttp(response);
        if (controller.signal.aborted) return;
        const nextCategories = Array.isArray(data) ? data : [];
        setCategories(nextCategories);
        setCategory((current) =>
          !current || nextCategories.includes(current) ? current : "",
        );
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          setError(errorToMessage(err));
        }
      });

    return () => controller.abort();
  }, [open, sector]);

  useEffect(() => {
    if (open && searchInput.trim() !== search) setLoading(true);
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [open, search, searchInput]);

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    setLoading(true);
    setError("");

    getIngredientCatalog({
      page,
      limit: CATALOG_PAGE_SIZE,
      name: search || undefined,
      category: category || undefined,
      sector: sector || undefined,
      signal: controller.signal,
    })
      .then((response) => {
        const result = normalizeList(response);
        setCatalog(result.rows);
        setTotalItems(result.totalItems);
        setTotalPages(result.totalPages);
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          setError(errorToMessage(err));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, page, category, sector, search]);

  function handleToggleRow(ingredientId) {
    const normalizedId = String(ingredientId);
    setSelectedIds((current) =>
      current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId],
    );
  }

  function handleClose() {
    if (!saving) onClose?.();
  }

  async function handleConfirm() {
    if (selectedIds.length === 0) return;

    setSaving(true);
    setError("");

    try {
      const response = unwrapHttp(
        await activateIngredientCatalogItems(selectedIds.map(Number))
      );
      const activated = Number(response?.activated);
      onConfirmed?.({
        activated: Number.isFinite(activated) ? activated : selectedIds.length,
        ingredientIds: Array.isArray(response?.ingredientIds)
          ? response.ingredientIds.map(Number).filter(Number.isSafeInteger)
          : [],
      });
    } catch (err) {
      setError(errorToMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: { bgcolor: "rgba(2, 8, 23, 0.45)", backdropFilter: "blur(3px)" },
        },
      }}
    >
      <Fade in={!!open} timeout={180}>
        <Box
          sx={{
            width: "min(94vw, 1050px)",
            maxHeight: "92vh",
            overflow: "hidden",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, md: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
          }}
        >
          <Stack spacing={1.8}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: "primary.900" }}>
                  Agregar insumos
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "neutral.700" }}>
                  Seleccioná los insumos disponibles que querés activar.
                </Typography>
              </Box>

              <IconButton onClick={handleClose} disabled={saving} aria-label="Cerrar modal">
                <CloseIcon sx={{ color: "primary.900" }} />
              </IconButton>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{
                width: "100%",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Select
                value={sector}
                onChange={(event) => {
                  setSector(event.target.value);
                  setCategory("");
                  setPage(1);
                }}
                displayEmpty
                size="small"
                inputProps={{ "aria-label": "Filtrar por sector" }}
                sx={{
                  width: { xs: "100%", sm: 220 },
                  flex: { sm: 1 },
                  minWidth: { sm: 220 },
                  height: 38,
                  borderRadius: "33px",
                  bgcolor: "neutral.100",
                  color: "primary.900",
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
                renderValue={(value) => value || "Todos los sectores"}
              >
                <MenuItem value="">Todos</MenuItem>
                {INGREDIENT_SECTORS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>

              <Select
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setPage(1);
                }}
                displayEmpty
                size="small"
                inputProps={{ "aria-label": "Filtrar por categoría" }}
                sx={{
                  width: { xs: "100%", sm: 240 },
                  flex: { sm: 1.15 },
                  minWidth: { sm: 240 },
                  height: 38,
                  borderRadius: "33px",
                  bgcolor: "neutral.100",
                  color: "primary.900",
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
                renderValue={(value) => value || "Todas las categorías"}
              >
                <MenuItem value="">Todas las categorías</MenuItem>
                {categories.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                fullWidth
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Buscar insumo"
                inputProps={{ "aria-label": "Buscar insumo" }}
                size="small"
                sx={{
                  flex: { sm: 2 },
                  minWidth: { sm: 280 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "primary.900", fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <ClearIcon
                        onClick={() => {
                          setSearchInput("");
                          setSearch("");
                          setPage(1);
                        }}
                        sx={{ color: "primary.900", fontSize: 18, cursor: "pointer" }}
                      />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Stack>

            {!!error && (
              <Box sx={{ px: 2, py: 1.2, borderRadius: "18px", bgcolor: "secondary.700" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                  No se pudo completar la operación
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco" }}>
                  {error}
                </Typography>
              </Box>
            )}

            <AddInsumosTable
              rows={catalog}
              selectedIds={selectedIds}
              loading={loading || saving}
              onToggleRow={handleToggleRow}
              height={{ xs: 300, md: "min(48vh, 430px)" }}
            />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
            >
              <Typography sx={{ minWidth: 190, fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                {loading
                  ? "Cargando catálogo…"
                  : `Mostrando ${catalog.length} de ${totalItems} insumos disponibles`}
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(_, nextPage) => setPage(nextPage)}
                  disabled={loading || saving}
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                  color="primary"
                />
              )}

              <Stack direction="row" spacing={1.2} sx={{ justifyContent: "flex-end" }}>
                <Button
                  onClick={handleClose}
                  disabled={saving}
                  sx={{
                    minWidth: 110,
                    height: 40,
                    borderRadius: "33px",
                    bgcolor: "neutral.100",
                    color: "primary.900",
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={selectedIds.length === 0 || loading || saving}
                  sx={{
                    minWidth: 145,
                    height: 40,
                    borderRadius: "33px",
                    bgcolor: "primary.500",
                    color: "logoblanco",
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": { bgcolor: "primary.600" },
                    "&.Mui-disabled": { bgcolor: "neutral.500", color: "neutral.700" },
                  }}
                >
                  {saving ? (
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <CircularProgress size={14} sx={{ color: "logoblanco" }} />
                      <span>Confirmando…</span>
                    </Stack>
                  ) : selectedIds.length > 0 ? (
                    `Confirmar (${selectedIds.length})`
                  ) : (
                    "Confirmar"
                  )}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Fade>
    </Dialog>
  );
}
